import React, {
  useState, useEffect, useMemo, useCallback, Fragment, ReactElement,
} from 'react';
import Table from '../../../../components/table/table.component';
import TableBody from '../../../../components/table-body/table-body.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import Widget from '../../../../components/widget/widget.component';
import LocationMap from '../../../../components/location-map/location-map.component';
import Button from '../../../../components/button/button.component';
import Hint from '../../../../components/hint/hint.component';
import Tooltip from '../../../../components/tooltip/tooltip.component';
import ExpandButton from '../../../../components/expand-button/expand-button.component';
import ActionButton from '../../../../components/action-button/action-button.component';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import FilteredLink from '../../../../components/filtered-link/filtered-link.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import FormFieldPhone from '../../../../components/form-field-phone/form-field-phone.component';
import FormFieldNumber from '../../../../components/form-field-number/form-field-number.component';
import FormFieldAddress from '../../../../components/form-field-address/form-field-address.component';
import FieldPhoneBlock from '../../../../components/field-phone-block/field-phone-block.component';
import FieldAddressBlock from '../../../../components/field-address-block/field-address-block.component';
import TableRowAddNewItem from '../../../../components/table-row-add-new-item/table-row-add-new-item.component';
import CloseIcon from '../../../../icons/close-icon';
import RemoveIcon from '../../../../icons/remove-icon';
import DownloadIcon from '../../../../icons/download-icon';
import FormatService from '../../../../services/format-service';
import FormService from '../../../../services/form-service';
import GoogleMapService from '../../../../services/google-map-service';
import { MARKER_COLOR_RED, MARKER_COLOR_BLUE } from '../../../../constants/entity/marker-colors';
import { CONTACT_TYPE_COMPANY, CONTACT_TYPE_PERSON } from '../../../../constants/entity/contact-types';
import { ROUTE_CONTACT } from '../../../../constants/system/routes';
import URL_LINKEDIN_PROFILE_PREFIX from '../../../../constants/system/urls';

const ContactWidgetContactInformation: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Contact Information';
  const WIDGET_NAME: string = 'widget-contact-information';
  const COLUMNS_TOTAL: number = 2;
  const COLUMNS_TOTAL_EDIT: number = 3;

  const {
    isEditMode,
    formPrefix,
    contactData,
    contactType,
    contactPersonData,
    contactCompanyData,
    contactAddressItems,
    contactAddressNewItemIds,
    contactAddressDetailsItems,
    relatedContactList,
    onPhoneChange,
    onPhoneWorkChange,
    onAddressFieldKeyDown,
    onAddressSearchCompleted,
    onEmailChange,
    onWebsiteChange,
    onContactConnectByEmail,
    onContactConnectByWebsite,
    onEditModeButtonClick,
    onContactAddressAddNewItem,
    onContactAddressDeleteItemClick,
    onContactAddressDetailsOpen,
    onContactAddressDetailsClose,
    onContactVCardDownloadButtonClick,
  } = props;

  const [isMapVisible, setIsMapVisible]: [boolean, Function] = useState(false);
  const [isContactConnectEmailOpen, setIsContactConnectEmailOpen]: [boolean, Function] = useState(true);
  const [isContactConnectWebsiteOpen, setIsContactConnectWebsiteOpen]: [boolean, Function] = useState(true);

  const contactId: number = contactData ? contactData.id : FormService.FORM_KEY_NEW;
  const mainContactLatitude: number | null = contactData.address
    ? GoogleMapService.extractLatitude(contactData.address) : null;
  const mainContactLongitude: number | null = contactData.address
    ? GoogleMapService.extractLongitude(contactData.address) : null;

  let locationMapIndex: number = 1;
  const contactLocationItemList: LocationMapItem[] = useMemo((): LocationMapItem[] => ([
    ...(
      mainContactLatitude && mainContactLongitude ? [{
        id: locationMapIndex++,
        title: contactData.fullName || null,
        color: MARKER_COLOR_RED,
        amount: null,
        link: `${ROUTE_CONTACT}/${contactData.id}`,
        imageLink: contactData.photoThumbnail || contactData.photo || null,
        latitude: mainContactLatitude,
        longitude: mainContactLongitude,
      }] : []
    ),
    ...(
      contactData.addresses && contactData.addresses.length
        ? contactData.addresses.map((contactAddress: ContactAddress, contactAddressIndex: number): LocationMapItem => ({
          id: locationMapIndex++,
          title: `${contactData.fullName} (${contactAddress.name
            ? `${contactAddress.name} Address` : `Address #${contactAddressIndex + 1}`})`,
          color: MARKER_COLOR_RED,
          amount: null,
          link: `${ROUTE_CONTACT}/${contactData.id}`,
          imageLink: contactData.photoThumbnail || contactData.photo || null,
          latitude: contactAddress.address ? GoogleMapService.extractLatitude(contactAddress.address) : null,
          longitude: contactAddress.address ? GoogleMapService.extractLongitude(contactAddress.address) : null,
        })) : []
    ),
    ...(
      relatedContactList && relatedContactList.length
        ? relatedContactList.map((contactItem: Contact): LocationMapItem => ({
          id: locationMapIndex++,
          title: contactItem.fullName || null,
          color: MARKER_COLOR_BLUE,
          amount: null,
          link: `${ROUTE_CONTACT}/${contactItem.id}`,
          imageLink: contactItem.photoThumbnail || contactItem.photo || null,
          latitude: contactItem.address ? GoogleMapService.extractLatitude(contactItem.address) : null,
          longitude: contactItem.address ? GoogleMapService.extractLongitude(contactItem.address) : null,
        })) : []
    ),
    ...(
      contactData.company && contactData.company.address ? [
        {
          id: locationMapIndex++,
          title: contactData.company.fullName || null,
          color: MARKER_COLOR_BLUE,
          amount: null,
          link: `${ROUTE_CONTACT}/${contactData.company.id}`,
          imageLink: contactData.company.photoThumbnail || contactData.company.photo || null,
          latitude: contactData.company.address
            ? GoogleMapService.extractLatitude(contactData.company.address) : null,
          longitude: contactData.company.address
            ? GoogleMapService.extractLongitude(contactData.company.address) : null,
        },
      ] : []
    ),
  ]), [contactData, mainContactLatitude, mainContactLongitude, locationMapIndex]);

  const withContactLocations: boolean = contactLocationItemList && !!contactLocationItemList.length;
  const isEmpty: boolean = !contactData || (!isEditMode && contactData && !contactData.email && !contactData.phone
    && !contactData.phoneText && !contactData.phoneWork && !contactData.phoneWorkText && !contactData.linkedin
    && !contactData.website && !contactData.streetAddress && !contactData.addressDetails
    && !withContactLocations && !isMapVisible);

  const addressClickDeps: DepsItem[] = useMemo((): DepsItem[] => [isMapVisible], [isMapVisible]);

  const onCloseContactConnectEmail: Function = useCallback((): void => {
    setIsContactConnectEmailOpen(false);
  }, []);

  const onCloseContactConnectWebsite: Function = useCallback((): void => {
    setIsContactConnectWebsiteOpen(false);
  }, []);

  const switchMapContainerVisibility: Function = useCallback((): void => {
    setIsMapVisible(!isMapVisible);
  }, [isMapVisible]);

  const emailChange: Function = useCallback((emailValue: string): void => {
    if (onEmailChange && contactType === CONTACT_TYPE_PERSON) {
      onEmailChange(emailValue);
    }
  }, [contactType]);

  const websiteChange: Function = useCallback((websiteValue: string): void => {
    if (onWebsiteChange && contactType === CONTACT_TYPE_COMPANY) {
      onWebsiteChange(websiteValue);
    }
  }, [contactType]);

  useEffect((): void => {
    setIsContactConnectEmailOpen(true);
  }, [contactPersonData]);

  useEffect((): void => {
    setIsContactConnectWebsiteOpen(true);
  }, [contactCompanyData]);

  return (
    <Widget
      isFullWidth
      isFullHeight
      withEditMode
      isEmpty={isEmpty}
      isEditMode={isEditMode}
      widgetName={WIDGET_NAME}
      defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
      onEditModeButtonClick={onEditModeButtonClick}
    >
      <Table>
        <TableBody isPlain withZebraRows>
          {
            (isEditMode || (!!contactData.phone && !!contactData.phoneText)) && (
              <TableRow>
                <TableCell>
                  Phone
                </TableCell>
                <TableCell colSpan={isEditMode ? COLUMNS_TOTAL_EDIT - 1 : 1}>
                  {
                    isEditMode ? (
                      <FieldPhoneBlock>
                        <FormFieldPhone
                          isBordered
                          isDisabled={!!contactData.user}
                          name={`${formPrefix}.phone`}
                          onChange={onPhoneChange}
                        />
                        <FormFieldNumber
                          isBordered
                          withNoCommas
                          isDisabled={!!contactData.user}
                          name={`${formPrefix}.phoneExtension`}
                          placeholder="Ext"
                        />
                      </FieldPhoneBlock>
                    ) : (
                      contactData.phone && contactData.phoneText && (
                        <FilteredLink
                          isPhone
                          link={FormatService.formatCleanIntegerNumberString(contactData.phone)}
                          phoneExtension={contactData.phoneExtension}
                        >
                          {
                            `${contactData.phoneText}`
                            + `${contactData.phoneExtension ? ` ext. ${contactData.phoneExtension}` : ''}`
                          }
                        </FilteredLink>
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || (!!contactData.phoneWork && !!contactData.phoneWorkText)) && (
              <TableRow>
                <TableCell>
                  Work Phone
                </TableCell>
                <TableCell colSpan={isEditMode ? COLUMNS_TOTAL_EDIT - 1 : 1}>
                  {
                    isEditMode ? (
                      <FieldPhoneBlock>
                        <FormFieldPhone
                          isBordered
                          isDisabled={!!contactData.user}
                          name={`${formPrefix}.phoneWork`}
                          onChange={onPhoneWorkChange}
                        />
                        <FormFieldNumber
                          isBordered
                          withNoCommas
                          isDisabled={!!contactData.user}
                          name={`${formPrefix}.phoneWorkExtension`}
                          placeholder="Ext"
                        />
                      </FieldPhoneBlock>
                    ) : (
                      contactData.phoneWork && contactData.phoneWorkText && (
                        <FilteredLink
                          isPhone
                          link={FormatService.formatCleanIntegerNumberString(contactData.phoneWork)}
                          phoneExtension={contactData.phoneWorkExtension}
                        >
                          {
                            `${contactData.phoneWorkText}${contactData.phoneWorkExtension
                              ? ` ext. ${contactData.phoneWorkExtension}` : ''}`
                          }
                        </FilteredLink>
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || !!contactData.email) && (
              <TableRow>
                <TableCell>
                  Email
                  {
                    isEditMode && contactType === CONTACT_TYPE_PERSON && (
                      <Hint
                        title={'Fill in Email field to search and automatically populate other fields '
                          + 'of the person, if it is listed in our dictionary'}
                      />
                    )
                  }
                </TableCell>
                <TableCell colSpan={isEditMode ? COLUMNS_TOTAL_EDIT - 1 : 1}>
                  {
                    isEditMode ? (
                      <Tooltip
                        isPersistent
                        isOpen={isContactConnectEmailOpen && !!contactPersonData}
                        title={(
                          <FlexContainer isColumn>
                            <FlexContainer isSpread>
                              <FlexContainer isInline>
                                Contact data was found by the email.
                              </FlexContainer>
                              <ActionButton
                                isLight
                                isInline
                                withOffset
                                withSmallPadding
                                onClick={onCloseContactConnectEmail}
                              >
                                <CloseIcon />
                              </ActionButton>
                            </FlexContainer>
                            <FlexContainer isStart>
                              <Button
                                isActive
                                isText
                                isNarrow
                                onClick={onContactConnectByEmail}
                              >
                                Connect
                              </Button>
                            </FlexContainer>
                          </FlexContainer>
                        )}
                      >
                        <FormFieldText
                          isBordered
                          isDisabled={!!contactData.user}
                          name={`${formPrefix}.email`}
                          onChange={(emailValue: string): void => {
                            emailChange(emailValue);
                          }}
                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            if (!contactPersonData) {
                              emailChange((event.target as HTMLInputElement).value);
                            }
                          }}
                        />
                      </Tooltip>
                    ) : (
                      contactData.email && (
                        <FilteredLink isEmail link={contactData.email}>
                          {contactData.email}
                        </FilteredLink>
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || !!contactData.linkedin) && (
              <TableRow>
                <TableCell>
                  LinkedIn
                </TableCell>
                <TableCell colSpan={isEditMode ? COLUMNS_TOTAL_EDIT - 1 : 1}>
                  {
                    isEditMode ? (
                      <FormFieldText
                        isBordered
                        name={`${formPrefix}.linkedin`}
                        placeholder={`${URL_LINKEDIN_PROFILE_PREFIX}...`}
                      />
                    ) : (
                      contactData.linkedin && (
                        <FilteredLink isExternal link={contactData.linkedin}>
                          {contactData.linkedin}
                        </FilteredLink>
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            contactType === CONTACT_TYPE_COMPANY && (isEditMode || !!contactData.website) && (
              <TableRow>
                <TableCell>
                  Website
                  {
                    isEditMode && (
                      <Hint
                        title={'Fill in Website field to search and automatically populate other fields '
                          + 'of the company, if it is listed in our dictionary'}
                      />
                    )
                  }
                </TableCell>
                <TableCell colSpan={isEditMode ? COLUMNS_TOTAL_EDIT - 1 : 1}>
                  {
                    isEditMode ? (
                      <Tooltip
                        isPersistent
                        isOpen={isContactConnectWebsiteOpen && !!contactCompanyData}
                        title={(
                          <FlexContainer isColumn>
                            <FlexContainer isSpread>
                              <FlexContainer isInline>
                                Contact data was found by the website.
                              </FlexContainer>
                              <ActionButton
                                isLight
                                isInline
                                withOffset
                                withSmallPadding
                                onClick={onCloseContactConnectWebsite}
                              >
                                <CloseIcon />
                              </ActionButton>
                            </FlexContainer>
                            <FlexContainer isStart>
                              <Button
                                isActive
                                isText
                                isNarrow
                                onClick={onContactConnectByWebsite}
                              >
                                Connect
                              </Button>
                            </FlexContainer>
                          </FlexContainer>
                        )}
                      >
                        <FormFieldText
                          isBordered
                          name={`${formPrefix}.website`}
                          onChange={(websiteValue: string): void => {
                            websiteChange(websiteValue);
                          }}
                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            if (!contactCompanyData) {
                              websiteChange((event.target as HTMLInputElement).value);
                            }
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <FilteredLink isExternal link={contactData.website}>
                        {contactData.website}
                      </FilteredLink>
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || !!contactData.streetAddress || withContactLocations) && (
              <TableRow>
                <TableCell>
                  <FilteredLink
                    isSwitcher={!isEditMode && withContactLocations}
                    clickDeps={addressClickDeps}
                    onClick={!isEditMode && withContactLocations ? switchMapContainerVisibility : null}
                  >
                    {isEditMode ? 'Main Address' : 'Address'}
                  </FilteredLink>
                </TableCell>
                <TableCell colSpan={isEditMode ? COLUMNS_TOTAL_EDIT - 1 : 1}>
                  {
                    isEditMode ? (
                      <FieldAddressBlock>
                        <FormFieldAddress
                          isBordered
                          itemId={contactId}
                          name={`${formPrefix}.streetAddress`}
                          onAddressSearchCompleted={(addressObject: JsonObject): void => {
                            onAddressSearchCompleted(addressObject);
                          }}
                          onKeyDown={(): void => {
                            onAddressFieldKeyDown();
                          }}
                        />
                        <FormFieldText isDisabled isBordered name={`${formPrefix}.location`} />
                        <FormFieldText isDisabled isBordered name={`${formPrefix}.postalCode`} />
                        <FormFieldText isHidden name={`${formPrefix}.address`} />
                      </FieldAddressBlock>
                    ) : (
                      contactData.streetAddress && (
                        <FilteredLink isExternal link={contactData.address.url}>
                          {
                            FormatService.formatStringAddress(
                              contactData.streetAddress,
                              contactData.location,
                              contactData.postalCode,
                            )
                          }
                        </FilteredLink>
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && isMapVisible && withContactLocations && (
              <TableRow>
                <TableCell colSpan={isEditMode ? COLUMNS_TOTAL_EDIT : COLUMNS_TOTAL}>
                  <LocationMap
                    defaultColor={MARKER_COLOR_RED}
                    locationItemList={contactLocationItemList}
                  />
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || !!contactData.addressDetails) && (
              <TableRow>
                <TableCell>
                  {isEditMode ? 'Main Address Details' : 'Address Details'}
                </TableCell>
                <TableCell colSpan={isEditMode ? COLUMNS_TOTAL_EDIT - 1 : 1}>
                  {
                    isEditMode ? (
                      <FormFieldText
                        isBordered
                        isMultiline
                        name={`${formPrefix}.addressDetails`}
                        maxRows={FormService.TEXTAREA_MAX_ROWS_DEFAULT}
                        minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
                      />
                    ) : (
                      contactData.addressDetails
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            ((contactAddressItems && Object.values(contactAddressItems)) || []).map((
              contactAddressItem: ContactAddress, contactAddressIndex: number,
            ): ReactElement => {
              const formKeyContactAddress: string = FormService.getIndexedFormFieldKey(contactAddressItem.id);
              const isNewItem: boolean = contactAddressNewItemIds && contactAddressNewItemIds.length
                ? contactAddressNewItemIds.includes(contactAddressItem.id) : false;
              const isAddressDetailsVisible: boolean = contactAddressDetailsItems && contactAddressDetailsItems.length
                ? contactAddressDetailsItems.includes(contactAddressItem.id) : false;

              return (
                <Fragment key={contactAddressItem.id}>
                  <TableRow isActive={isNewItem}>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FlexContainer>
                            <FlexContainer>
                              <ExpandButton
                                isOpen={isAddressDetailsVisible}
                                onClick={(): void => {
                                  if (isAddressDetailsVisible) {
                                    onContactAddressDetailsClose(contactAddressItem.id);
                                  } else {
                                    onContactAddressDetailsOpen(contactAddressItem.id);
                                  }
                                }}
                              />
                            </FlexContainer>
                            <FormFieldText isBordered name={`${formPrefix}.addresses[${formKeyContactAddress}].name`} />
                          </FlexContainer>
                        ) : (
                          (contactAddressItem.name && `${contactAddressItem.name} Address`)
                          || (`Address #${contactAddressIndex + 1}`)
                        )
                      }
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FieldAddressBlock>
                            <FormFieldAddress
                              isBordered
                              itemId={`${contactId}-${contactAddressItem.id}`}
                              name={`${formPrefix}.addresses[${formKeyContactAddress}].streetAddress`}
                              onAddressSearchCompleted={(addressObject: JsonObject): void => {
                                onAddressSearchCompleted(addressObject, contactAddressItem.id);
                              }}
                              onKeyDown={(): void => {
                                onAddressFieldKeyDown(contactAddressItem.id);
                              }}
                            />
                            <FormFieldText
                              isDisabled
                              isBordered
                              name={`${formPrefix}.addresses[${formKeyContactAddress}].location`}
                            />
                            <FormFieldText
                              isDisabled
                              isBordered
                              name={`${formPrefix}.addresses[${formKeyContactAddress}].postalCode`}
                            />
                            <FormFieldText
                              isHidden
                              name={`${formPrefix}.addresses[${formKeyContactAddress}].id`}
                            />
                            <FormFieldText
                              isHidden
                              name={`${formPrefix}.addresses[${formKeyContactAddress}].address`}
                            />
                          </FieldAddressBlock>
                        ) : (
                          contactAddressItem.streetAddress && (
                            <FilteredLink isExternal link={contactAddressItem.address.url}>
                              {
                                FormatService.formatStringAddress(
                                  contactAddressItem.streetAddress,
                                  contactAddressItem.location,
                                  contactAddressItem.postalCode,
                                )
                              }
                            </FilteredLink>
                          )
                        )
                      }
                    </TableCell>
                    {
                      isEditMode && (
                        <TableCell>
                          <Tooltip title="Delete">
                            <ActionButton
                              isInline
                              withOffset
                              withSmallPadding
                              onClick={(): void => {
                                onContactAddressDeleteItemClick(contactAddressItem.id);
                              }}
                            >
                              <RemoveIcon />
                            </ActionButton>
                          </Tooltip>
                        </TableCell>
                      )
                    }
                  </TableRow>
                  {
                    isAddressDetailsVisible && (
                      <TableRow isActive={isNewItem}>
                        <TableCell>
                          {
                            !isEditMode ? ((contactAddressItem.name && `${contactAddressItem.name} Address Details`)
                              || (`Address #${contactAddressIndex + 1} Details`)) : 'Address Details'
                          }
                        </TableCell>
                        <TableCell colSpan={isEditMode ? COLUMNS_TOTAL_EDIT - 1 : 1}>
                          {
                            isEditMode ? (
                              <FormFieldText
                                isBordered
                                isMultiline
                                name={`${formPrefix}.addresses[${formKeyContactAddress}].addressDetails`}
                                maxRows={FormService.TEXTAREA_MAX_ROWS_DEFAULT}
                                minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
                              />
                            ) : (
                              contactAddressItem.addressDetails
                            )
                          }
                        </TableCell>
                      </TableRow>
                    )
                  }
                </Fragment>
              );
            })
          }
          {
            isEditMode && (
              <TableRowAddNewItem
                colSpan={isEditMode ? COLUMNS_TOTAL_EDIT : COLUMNS_TOTAL}
                label="Add New Address"
                onClick={onContactAddressAddNewItem}
              />
            )
          }
          {
            (!isEditMode && contactData.company && contactData.company.streetAddress) && (
              <TableRow>
                <TableCell>
                  Company Address
                </TableCell>
                <TableCell colSpan={isEditMode ? COLUMNS_TOTAL_EDIT - 1 : 1}>
                  <FilteredLink isExternal link={contactData.company.address.url}>
                    {
                      FormatService.formatStringAddress(
                        contactData.company.streetAddress,
                        contactData.company.location,
                        contactData.company.postalCode,
                      )
                    }
                  </FilteredLink>
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && (
              <TableRow>
                <TableCell>
                  vCard File
                </TableCell>
                <TableCell colSpan={isEditMode ? COLUMNS_TOTAL_EDIT - 1 : 1}>
                  <Button isActive isText isMediumSize onClick={onContactVCardDownloadButtonClick}>
                    <DownloadIcon />
                    {' '}
                    Download vCard
                  </Button>
                </TableCell>
              </TableRow>
            )
          }
        </TableBody>
      </Table>
    </Widget>
  );
});

export default ContactWidgetContactInformation;
